// Calculator.js
import React, { useState, useEffect } from 'react';
import { combination, InfoTextField } from './Util';
import {
  Box,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Tooltip,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

/**
 * Computes the drop rate based on given modifiers.
 *
 * @param {number} baseRate The base drop rate.
 * @param {number} prospection The prospection value.
 * @param {number} anomaly The anomaly modifier percentage.
 * @param {number} almanax The daily almanax modifier percentage.
 * @param {number[]} challenges Array of challenge modifier percentages.
 * @param {boolean} ankamaShield Whether the Ankama Shield bonus applies.
 * @return {number} The computed final drop rate.
 */
function calculateDropRate(
  baseRate,
  prospection,
  anomaly,
  almanax,
  challenges,
  ankamaShield
) {
  let prospectionMultiplier = 1;
  if (prospection >= 0) {
    prospectionMultiplier =
      0.74 + 1.755 / (1 + Math.exp((250 - prospection) / 85));
    prospectionMultiplier = Math.max(Math.min(prospectionMultiplier, 2.5), 1);
  }
  const anomalyMultiplier = 1 + anomaly / 100;
  const almanaxMultiplier = 1 + almanax / 100;
  const challengeMultiplier = challenges.reduce(
    (acc, mod) => acc * (1 + mod / 100),
    1
  );
  const ankamaMultiplier = ankamaShield ? 1.05 : 1;
  return Math.min(
    Math.max(baseRate, 0) *
      prospectionMultiplier *
      anomalyMultiplier *
      almanaxMultiplier *
      challengeMultiplier *
      ankamaMultiplier,
    100
  );
}

function Calculator() {
  // Default values
  const [baseRate, setBaseRate] = useState('10');
  const [prospection, setProspection] = useState('0');
  const [anomaly, setAnomaly] = useState('0');
  const [almanax, setAlmanax] = useState('0');
  const [challenge1, setChallenge1] = useState('0');
  const [challenge2, setChallenge2] = useState('0');
  const [challenge3, setChallenge3] = useState('0');
  const [ankamaShield, setAnkamaShield] = useState(false);
  const [result, setResult] = useState(null);
  const [monsters, setMonsters] = useState('1'); // Number of monsters in fight

  useEffect(() => {
    const base = parseFloat(baseRate);
    const pros = parseFloat(prospection);
    const anom = parseFloat(anomaly);
    const dail = parseFloat(almanax);
    const challenges = [challenge1, challenge2, challenge3]
      .map(val => parseFloat(val))
      .filter(val => !isNaN(val));

    if (!isNaN(base) && !isNaN(pros) && !isNaN(anom) && !isNaN(dail)) {
      const dropRate = calculateDropRate(
        base,
        pros,
        anom,
        dail,
        challenges,
        ankamaShield
      );
      setResult(dropRate);
    } else {
      setResult(null);
    }
  }, [
    baseRate,
    prospection,
    anomaly,
    almanax,
    challenge1,
    challenge2,
    challenge3,
    ankamaShield,
  ]);

  // Compute loot chances using the binomial distribution.
  const monstersInt = parseInt(monsters, 10);
  let lootChances = [];
  if (result !== null && !isNaN(monstersInt) && monstersInt > 0) {
    const p = result / 100;
    for (let k = 0; k <= monstersInt; k++) {
      const prob =
        combination(monstersInt, k) *
        Math.pow(p, k) *
        Math.pow(1 - p, monstersInt - k);
      lootChances.push({ k, prob });
    }
  }

  // Common sx style for TextFields: border color = header color (#333), text white.
  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#333',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#333',
      },
    },
    input: {
      color: '#fff',
    },
    label: {
      color: '#fff',
    },
  };

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InfoTextField
            fullWidth
            label="Base Rate (%)"
            tooltip="The Base drop rate percentage before any modifiers are applied as indicated in the Bestiary Database."
            type="number"
            value={baseRate}
            onChange={(e) => setBaseRate(e.target.value)}
            required
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoTextField
            fullWidth
            label="Prospection Value"
            tooltip="The Character's prospection score."
            type="number"
            value={prospection}
            onChange={(e) => setProspection(e.target.value)}
            required
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={ankamaShield}
                onChange={(e) => setAnkamaShield(e.target.checked)}
                sx={{
                  color: '#fff',
                  '&.Mui-checked': { color: '#fff' },
                }}
              />
            }
            label={
              <span>
                Ankama Shield (+5%)
                <Tooltip title="Ankama shield provides an additional 5% bonus to the drop rate, in-game displayed value includes both this modifier and prospection">
                  <InfoIcon sx={{ fontSize: 16, ml: 0.5, verticalAlign: 'middle' }} />
                </Tooltip>
              </span>
            }
            labelPlacement="start"
            sx={{ color: '#fff' }}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoTextField
            fullWidth
            label="Anomaly Modifier (%)"
            tooltip="Percentage bonus from anomaly modifiers."
            type="number"
            value={anomaly}
            onChange={(e) => setAnomaly(e.target.value)}
            required
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoTextField
            fullWidth
            label="Almanax Modifier (%)"
            tooltip="Daily almanax bonus percentage (if any)."
            type="number"
            value={almanax}
            onChange={(e) => setAlmanax(e.target.value)}
            required
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoTextField
            fullWidth
            label="Challenge Modifier (%)"
            tooltip="Bonus from the challenge modifier."
            type="number"
            value={challenge1}
            onChange={(e) => setChallenge1(e.target.value)}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoTextField
            fullWidth
            label="Challenge Modifier 2 (%)"
            tooltip="Bonus from a second challenge modifier (if any)."
            type="number"
            value={challenge2}
            onChange={(e) => setChallenge2(e.target.value)}
            sx={textFieldSx}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoTextField
            fullWidth
            label="Challenge Modifier 3 (%)"
            tooltip="Bonus from a third challenge modifier (if any)."
            type="number"
            value={challenge3}
            onChange={(e) => setChallenge3(e.target.value)}
            sx={textFieldSx}
          />
        </Grid>
      </Grid>
      {result !== null && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h5" sx={{ color: '#fff' }}>
            Calculated Drop Rate: {result.toFixed(2)}%
          </Typography>
          <InfoTextField
            fullWidth
            label="Number of Monsters in Fight"
            tooltip="Enter the total number of monsters that drop the loot you want in current combat."
            type="number"
            value={monsters}
            onChange={(e) => setMonsters(e.target.value)}
            sx={{ mt: 2, ...textFieldSx }}
          />
          {lootChances.length > 0 && (
            <TableContainer
              component={Paper}
              sx={{
                mt: 3,
                backgroundColor: '#474747',
                border: '1px solid #333',
              }}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    {lootChances.map(({ k }) => (
                      <TableCell
                        align="center"
                        key={`loot-${k}`}
                        sx={{
                          border: '1px solid #333',
                          color: '#fff',
                        }}
                      >
                        {k}
                      </TableCell>
                    ))}
                  </TableRow>
                  <TableRow>
                    {lootChances.map(({ k, prob }) => (
                      <TableCell
                        align="center"
                        key={`prob-${k}`}
                        sx={{
                          border: '1px solid #333',
                          color: '#fff',
                        }}
                      >
                        {(prob * 100).toFixed(2)}%
                      </TableCell>
                    ))}
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}
    </Box>
  );
}

export default Calculator;
